import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEvent, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BiInfoCircle, BiSend } from 'react-icons/bi';
import { useNavigate } from 'react-router';

// import type { Person } from '@/store/persons/person.dto';
// import { usePerson } from '@/store/persons/person.store';
import { Avatar } from '@/components/Avatar';
import { Button } from '@/components/Button';
import { Autocomplete, ContactInput, Input, Select } from '@/components/Inputs';
import {
  guestContactOptions,
  pickedGuestContacts,
  preferredChannel,
} from '@/store/contacts/contacts.mappers';
import { useContacts } from '@/store/contacts/contacts.store';
import type { GuestInvitee } from '@/store/invites/invites.store';
import { toast } from '@/utils/toast';

import Modal from '../Modal/Modal';

import type { InviteCrewMember } from './guestInvitation.schema';
import { guestInvitationSchema } from './guestInvitation.schema';
import s from './GuestInvitationModal.module.scss';

export type { InviteCrewMember };

type GuestInvitationModalProps = {
  onClose(): void;
  onInvite?(values: GuestInvitee[]): Promise<void> | void;
  eventName?: string;
  /** People already on the event — hidden from the picker so nobody is invited twice. */
  excludeIds?: string[];
};

// const personName = (person: Person) =>
//   [person.person_info?.first_name, person.person_info?.last_name].filter(Boolean).join(' ').trim();
//
// const personContact = (person: Person) => {
//   const contacts = person.contacts ?? [];
//   return (
//     contacts.find(({ contact_type }) => contact_type === 'email')?.contact ??
//     contacts.find(({ contact_type }) => contact_type === 'phone')?.contact ??
//     ''
//   );
// };

export const GuestInvitationModal = ({
  onClose,
  onInvite,
  eventName,
}: GuestInvitationModalProps) => {
  const navigate = useNavigate();
  // const people = usePerson((state) => state.people);
  // const getPeople = usePerson((state) => state.getPeople);

  // const [pickedId, setPickedId] = useState<string | null>(null);

  const contacts = useContacts((state) => state.contacts);
  const getContacts = useContacts((state) => state.getContacts);
  const [pickedContactId, setPickedContactId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [pickedIds, setPickedIds] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    void getContacts();
  }, [getContacts]);

  const contactOptions = guestContactOptions(contacts);
  const search = query.trim().toLowerCase();
  const searchOptions = contactOptions
    .filter((option) => !pickedIds.includes(option.value))
    .filter((option) => option.name.toLowerCase().includes(search))
    .map((option) => ({
      value: option.value,
      disabled: option.disabled,
      label: (
        <>
          <Avatar src={option.avatar} firstName={option.name} size={28} />
          {option.label}
        </>
      ),
    }));
  const selectedOptions = contactOptions
    .filter((option) => pickedIds.includes(option.value))
    .map((option) => ({ value: option.value, label: option.name }));

  const {
    handleSubmit,
    control,
    register,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(guestInvitationSchema),
    defaultValues: {
      contact: '',
    },
  });

  // useEffect(() => {
  //   if (!people) void getPeople();
  // }, [people, getPeople]);
  //
  // const candidates = useMemo(
  //   () => (people ?? []).filter((person) => !excludeIds.includes(person.id)),
  //   [people, excludeIds],
  // );
  //
  // const options = useMemo(
  //   () =>
  //     candidates.map((person) => ({
  //       value: person.id,
  //       label: personName(person) || personContact(person) || 'Unknown',
  //       disabled: !personContact(person),
  //     })),
  //   [candidates],
  // );
  //
  // const pick = (personId: string | null) => {
  //   const person = candidates.find(({ id }) => id === personId);
  //   const contact = person ? personContact(person) : '';
  //   if (!person || !contact) return;
  //
  //   setPickedId(person.id);
  //   setValue('name', personName(person), { shouldValidate: true });
  //   setValue('contact', contact, { shouldValidate: true });
  // };

  const pickContact = (id: string | null) => {
    const picked = contacts.find((contact) => contact.id === id);
    const channel = picked && preferredChannel(picked);
    if (!picked || !channel) return;
    setPickedContactId(picked.id);
    setValue('name', picked.name, { shouldValidate: true });
    setValue('contact', channel.contact, { shouldValidate: true });
  };

  const inviteAll = async (recipients: GuestInvitee[]) => {
    if (!onInvite) return;
    setSending(true);
    try {
      await onInvite(recipients);
    } catch (err: unknown) {
      console.error(err);
      toast.error('Couldn’t send the invitations. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    const { name, contact } = getValues();
    if (onInvite && pickedIds.length > 0 && !name?.trim() && !contact.trim()) {
      event.preventDefault();
      void inviteAll(pickedGuestContacts(contacts, pickedIds));
      return;
    }
    void handleSubmit(invite)(event);
  };

  const invite = async ({ contact, name }: InviteCrewMember) => {
    try {
      if (onInvite) {
        await inviteAll([...pickedGuestContacts(contacts, pickedIds), { contact, name }]);
        return;
      }
      navigate(
        `/invite-guest?contact=${encodeURIComponent(contact)}&name=${encodeURIComponent(name)}`,
      );
      onClose();
    } catch (err: unknown) {
      console.error(err);
    }
  };

  return (
    <Modal open title="Invite Guest" onClose={onClose}>
      <section className={s.modalContent}>
        {eventName && <p className={s.eventName}>{eventName}</p>}
        <p>Your guest will use a link in email or SMS to join.</p>

        {/* {options.length > 0 && (
          <>
            <Select
              label="Someone on this vessel"
              placeholder="Choose a person"
              options={options}
              value={pickedId}
              onChange={pick}
            />
            <p className={s.or}>or invite by contact</p>
          </>
        )} */}

        <form className={s.inviteForm} onSubmit={submit}>
          {onInvite && (
            <Autocomplete
              label="Contacts"
              placeholder="Search Contacts"
              value={query}
              onChange={setQuery}
              options={searchOptions}
              selectedOptions={selectedOptions}
              onMultipleItemsSelect={(selected) => {
                setPickedIds(selected.map(({ value }) => value));
                setQuery('');
              }}
            />
          )}
          {!onInvite && contacts.length > 0 && (
            <Select
              label="From Contacts"
              placeholder="Choose a contact"
              options={guestContactOptions(contacts)}
              value={pickedContactId}
              onChange={pickContact}
            />
          )}
          <Input
            label="Guest Name"
            placeholder="Alex Morgan"
            error={errors.name?.message}
            {...register('name')}
            mandatory={pickedIds.length === 0}
          />
          <Controller
            control={control}
            name="contact"
            render={({ field }) => (
              <ContactInput
                label="Mobile or Email"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.contact?.message}
                className={s.stretch}
                mandatory={pickedIds.length === 0}
              />
            )}
          />
          <Button type="submit" disabled={isSubmitting || sending}>
            {isSubmitting || sending ? 'Sending…' : 'Send Invitation'}{' '}
            <BiSend color="#fff" size={20} />
          </Button>
        </form>
        <div className={s.expirationInfo}>
          <BiInfoCircle size={20} />
          The link expires 1 day before the voyage starts.
        </div>
      </section>
    </Modal>
  );
};
